/**
 * StageDrawingsRegister — generic drawings register for downstream
 * stages (C, D, E, F, G, H, I, J).  Mirrors the UX shipped for B.2
 * PFDs (`PfdDocumentFirstPanel`) and B.3 P&IDs (`PidDocumentRegister`):
 *
 *   • Documents tab  — list every SVG deliverable produced by the
 *                       stage (vessel GAs, exchanger GAs, piping
 *                       isometrics, loop diagrams, foundation plans,
 *                       etc.) with one row per drawing.
 *   • Viewer tab     — full-pane SVG renderer with pan / zoom /
 *                       fullscreen for whichever drawing the user
 *                       opens from the Documents tab.
 *   • Edit jump      — routes to the existing stage-specific editor
 *                       (DisciplineCanvasEditor, PfdEditor, …)
 *                       via the onOpenEditor callback.
 *
 * The backend already emits SVG-format deliverables for every stage
 * specialist (process_plant_a6_engine, stage_c_drawings,
 * stage_defg_drawings, etc.) and persists them with stage_code +
 * format=svg in the `deliverables` collection. This component is the
 * universal viewer over that data — no new backend endpoint required.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { FileText, Eye, Pencil, RefreshCcw, ZoomIn, ZoomOut,
         Maximize2, Minimize2, Loader2, AlertCircle, FileCode,
         Download } from "lucide-react";

const COL = {
   bg: "var(--pf-bg, #020617)",
   surface: "var(--pf-surface, #0f1729)",
   border: "var(--pf-border, #1e293b)",
   text: "var(--pf-text, #e2e8f0)",
   dim: "var(--pf-text-muted, #94a3b8)",
   spark: "var(--pf-spark, #22d3ee)",
   amber: "#f59e0b",
   green: "#22c55e",
};
const mono = { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" };

export default function StageDrawingsRegister({ projectId, stageCode,
                                                 stageName, onOpenEditor,
                                                 editorTabLabel }) {
   const [activeTab, setActiveTab] = useState("documents"); // documents | viewer
   const [delivs, setDelivs] = useState([]);
   const [loading, setLoading] = useState(false);
   const [err, setErr] = useState("");
   const [openCode, setOpenCode] = useState(null);
   const [zoom, setZoom] = useState(1);
   const [pan, setPan] = useState({ x: 0, y: 0 });
   const [panning, setPanning] = useState(false);
   const [fullscreen, setFullscreen] = useState(false);
   const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

   const load = useCallback(async () => {
      if (!projectId || !stageCode) return;
      setLoading(true); setErr("");
      try {
         // Wave CO — query stage deliverables, then ALSO every L2/L3
         // sub-stage of this parent so the user sees every SVG produced
         // across the stage tree (e.g. all D.1 → D.6 isometrics under D).
         const codes = [stageCode];
         try {
            const stages = await api.get(`/projects/${projectId}/stages`);
            const all = stages?.data?.stages || stages?.data || [];
            const prefix = stageCode + ".";
            for (const s of all) {
               if (s?.code && typeof s.code === "string"
                   && s.code.startsWith(prefix)) codes.push(s.code);
            }
         } catch { /* ignored — fallback to L1 only */ }
         const results = await Promise.all(codes.map((c) =>
            api.get(`/projects/${projectId}/stages/${encodeURIComponent(c)}/deliverables`)
               .then((r) => Array.isArray(r.data) ? r.data : [])
               .catch(() => [])
         ));
         const merged = [].concat(...results);
         // Keep SVG-format only — that is the user's "drawings tab"
         // requirement. De-dupe by code keeping the newest version.
         const byCode = new Map();
         for (const d of merged) {
            if ((d.format || "").toLowerCase() !== "svg") continue;
            const have = byCode.get(d.code);
            if (!have || (d.version || 0) > (have.version || 0)) {
               byCode.set(d.code, d);
            }
         }
         const list = Array.from(byCode.values()).sort(
            (a, b) => (a.code || "").localeCompare(b.code || ""));
         setDelivs(list);
      } catch (e) {
         setErr(e?.message || String(e));
      } finally {
         setLoading(false);
      }
   }, [projectId, stageCode]);

   // eslint-disable-next-line
   useEffect(() => { load(); }, [load]);

   // Default-select the first drawing if none chosen. Derived value
   // (no setState in effect) — recomputed on each render which is
   // cheap given list size.
   const effectiveOpenCode = openCode
      || (delivs.length ? delivs[0].code : null);
   const current = useMemo(
      () => delivs.find((d) => d.code === effectiveOpenCode) || null,
      [delivs, effectiveOpenCode]);

   const onOpen = (code) => {
      setOpenCode(code);
      setActiveTab("viewer");
      setZoom(1); setPan({ x: 0, y: 0 });
   };

   const onEdit = () => {
      if (onOpenEditor) onOpenEditor();
   };

   const onWheel = (e) => {
      if (e.shiftKey) return;
      e.preventDefault();
      const step = (e.ctrlKey || e.metaKey) ? 0.05 : 0.15;
      setZoom((z) => Math.max(0.2, Math.min(4,
         +(z + (e.deltaY > 0 ? -step : step)).toFixed(2))));
   };
   const onMouseDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      setPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY,
         panX: pan.x, panY: pan.y };
   };
   const onMouseMove = (e) => {
      if (!panning) return;
      setPan({
         x: panStart.current.panX + (e.clientX - panStart.current.x),
         y: panStart.current.panY + (e.clientY - panStart.current.y),
      });
   };
   const onMouseUp = () => { setPanning(false); };

   useEffect(() => {
      if (!fullscreen) return undefined;
      const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [fullscreen]);

   const downloadSvg = () => {
      if (!current?.content_md) return;
      const blob = new Blob([current.content_md], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${current.code || "drawing"}.svg`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
         document.body.removeChild(a);
         URL.revokeObjectURL(url);
      }, 200);
   };

   const viewerContainerStyle = fullscreen ? {
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 100, background: COL.bg,
   } : { flex: 1, minHeight: 480, display: "flex", flexDirection: "column" };

   return (
      <div data-testid={`stage-drawings-register-${stageCode}`}
            style={{ color: COL.text, ...mono, height: "100%",
                      display: "flex", flexDirection: "column" }}>
         {/* Header */}
         <div style={{ borderBottom: `1px solid ${COL.border}`,
                          padding: "12px 20px", display: "flex",
                          alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700,
                            letterSpacing: 0.6 }}>
               {stageCode} — {stageName?.toUpperCase() || "DRAWINGS"}
            </h2>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
               <button data-testid={`drawings-tab-documents-${stageCode}`}
                       onClick={() => setActiveTab("documents")}
                       style={tabBtn(activeTab === "documents")}>
                  <FileText size={12} style={{ display: "inline",
                                                  marginRight: 4 }}/>
                  Documents <span style={{ color: COL.dim,
                                              marginLeft: 6 }}>
                     {delivs.length}
                  </span>
               </button>
               <button data-testid={`drawings-tab-viewer-${stageCode}`}
                       onClick={() => setActiveTab("viewer")}
                       style={tabBtn(activeTab === "viewer")}>
                  <Eye size={12} style={{ display: "inline",
                                            marginRight: 4 }}/>
                  Viewer
               </button>
               <button data-testid={`drawings-refresh-${stageCode}`}
                       onClick={load}
                       title="Refresh drawing register"
                       style={btnSecondary}>
                  <RefreshCcw size={12} style={{ display: "inline",
                                                    marginRight: 4 }}/>
                  Refresh
               </button>
            </div>
         </div>

         {err && (
            <div style={{ background: "#7f1d1d", padding: 10,
                            color: "#fee2e2", fontSize: 12 }}>
               <AlertCircle size={12} style={{ display: "inline",
                                                  marginRight: 6 }}/>
               {err}
            </div>
         )}

         {loading && !delivs.length && (
            <div style={{ flex: 1, display: "flex", alignItems: "center",
                            justifyContent: "center", color: COL.dim }}>
               <Loader2 size={18} style={{ marginRight: 8,
                                             animation: "spin 1s linear infinite" }}/>
               Loading drawings…
            </div>
         )}

         {!loading && delivs.length === 0 && (
            <div data-testid={`drawings-empty-${stageCode}`}
                  style={{ flex: 1, display: "flex",
                            flexDirection: "column", alignItems: "center",
                            justifyContent: "center", gap: 12,
                            color: COL.dim, textAlign: "center",
                            fontSize: 13, padding: 24 }}>
               <FileCode size={28} style={{ color: COL.spark,
                                              opacity: 0.6 }}/>
               <div>No drawings yet for stage {stageCode}.</div>
               <div style={{ fontSize: 11 }}>
                  Run this stage from the <b>Documents</b> tab — every
                  CAD-style SVG produced will appear here as a separate
                  drawing row that you can view or edit.
               </div>
            </div>
         )}

         {activeTab === "documents" && delivs.length > 0 && (
            <div style={{ flex: 1, overflow: "auto" }}>
               <table style={{ width: "100%", borderCollapse: "collapse",
                                  fontSize: 12 }}>
                  <thead>
                     <tr style={{ background: COL.surface }}>
                        <th style={thStyle}>CODE</th>
                        <th style={thStyle}>DRAWING</th>
                        <th style={thStyle}>FORMAT</th>
                        <th style={thStyle}>VER</th>
                        <th style={thStyle}>SIZE</th>
                        <th style={thStyle}>ACTION</th>
                     </tr>
                  </thead>
                  <tbody>
                     {delivs.map((d, idx) => {
                        const svgBytes = (d.content_md || "").length;
                        return (
                           <tr key={`${d.code}-${d.id || idx}`}
                               data-testid={`drawings-row-${d.code}`}
                               style={{ borderBottom:
                                          `1px solid ${COL.border}` }}>
                              <td style={{ ...tdStyle, color: COL.spark,
                                             fontWeight: 600 }}>{d.code}</td>
                              <td style={tdStyle}>
                                 <div>{d.name}</div>
                                 {d.summary && (
                                    <div style={{ color: COL.dim,
                                                    fontSize: 10,
                                                    marginTop: 4 }}>
                                       {d.summary}
                                    </div>
                                 )}
                              </td>
                              <td style={{ ...tdStyle, color: COL.amber,
                                             fontSize: 10 }}>
                                 SVG
                              </td>
                              <td style={{ ...tdStyle, color: COL.dim }}>
                                 v{d.version || 1}
                              </td>
                              <td style={{ ...tdStyle, color: COL.dim,
                                             fontSize: 10 }}>
                                 {(svgBytes / 1024).toFixed(1)} KB
                              </td>
                              <td style={tdStyle}>
                                 <div style={{ display: "flex", gap: 4,
                                                  flexWrap: "wrap" }}>
                                    <button
                                       data-testid={`drawings-view-${d.code}`}
                                       onClick={() => onOpen(d.code)}
                                       style={btnSmall}>
                                       <Eye size={11} style={{ display: "inline",
                                                                  marginRight: 4 }}/>
                                       View
                                    </button>
                                    {onOpenEditor && (
                                       <button
                                          data-testid={`drawings-edit-${d.code}`}
                                          onClick={() => {
                                             setOpenCode(d.code);
                                             onEdit();
                                          }}
                                          title={editorTabLabel
                                             ? `Open in ${editorTabLabel}`
                                             : "Open in editor"}
                                          style={{ ...btnSmall,
                                             background: "rgba(34,211,238,0.15)",
                                             color: COL.spark,
                                             border: `1px solid ${COL.spark}` }}>
                                          <Pencil size={11} style={{ display: "inline",
                                                                        marginRight: 4 }}/>
                                          Edit
                                       </button>
                                    )}
                                 </div>
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
               </table>
            </div>
         )}

         {activeTab === "viewer" && (
            <div style={viewerContainerStyle}>
               <div style={{ display: "flex", gap: 8,
                                padding: "8px 12px",
                                background: COL.surface,
                                borderBottom: `1px solid ${COL.border}`,
                                alignItems: "center", flexWrap: "wrap" }}>
                  <select data-testid={`drawings-viewer-select-${stageCode}`}
                          value={effectiveOpenCode || ""}
                          onChange={(e) => {
                             setOpenCode(e.target.value);
                             setZoom(1); setPan({ x: 0, y: 0 });
                          }}
                          style={selectStyle}>
                     {delivs.map((d, idx) => (
                        <option key={`${d.code}-${d.id || idx}`}
                                value={d.code}>
                           {d.code} · {d.name}
                        </option>
                     ))}
                  </select>
                  <button onClick={() => setZoom((z) =>
                              Math.max(0.2, +(z - 0.15).toFixed(2)))}
                          title="Zoom out"
                          data-testid={`drawings-zoom-out-${stageCode}`}
                          style={iconBtn}>
                     <ZoomOut size={13}/>
                  </button>
                  <span style={{ fontSize: 11, color: COL.dim,
                                    minWidth: 40, textAlign: "center" }}>
                     {Math.round(zoom * 100)}%
                  </span>
                  <button onClick={() => setZoom((z) =>
                              Math.min(4, +(z + 0.15).toFixed(2)))}
                          title="Zoom in"
                          data-testid={`drawings-zoom-in-${stageCode}`}
                          style={iconBtn}>
                     <ZoomIn size={13}/>
                  </button>
                  <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                          title="Reset"
                          data-testid={`drawings-zoom-reset-${stageCode}`}
                          style={iconBtn}>
                     1:1
                  </button>
                  <div style={{ flex: 1 }}/>
                  {current && (
                     <button onClick={downloadSvg}
                             title="Download SVG"
                             data-testid={`drawings-download-${stageCode}`}
                             style={iconBtn}>
                        <Download size={13}/>
                     </button>
                  )}
                  <button onClick={() => setFullscreen((v) => !v)}
                          title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                          data-testid={`drawings-fullscreen-${stageCode}`}
                          style={iconBtn}>
                     {fullscreen ? <Minimize2 size={13}/> : <Maximize2 size={13}/>}
                  </button>
                  {onOpenEditor && current && (
                     <button data-testid={`drawings-viewer-edit-${stageCode}`}
                             onClick={onEdit}
                             title={editorTabLabel
                                ? `Open in ${editorTabLabel}`
                                : "Open in editor"}
                             style={{ ...iconBtn,
                                background: "rgba(34,211,238,0.15)",
                                color: COL.spark,
                                border: `1px solid ${COL.spark}`,
                                padding: "5px 10px",
                                display: "flex", alignItems: "center", gap: 4 }}>
                        <Pencil size={12}/>
                        <span style={{ fontSize: 11 }}>Edit</span>
                     </button>
                  )}
               </div>
               <div style={{ flex: 1, minHeight: 0, position: "relative",
                                overflow: "hidden",
                                cursor: panning ? "grabbing" : "grab",
                                background: "#fafafa" }}
                     onWheel={onWheel}
                     onMouseDown={onMouseDown}
                     onMouseMove={onMouseMove}
                     onMouseUp={onMouseUp}
                     onMouseLeave={onMouseUp}>
                  {current?.content_md ? (
                     <div data-testid={`drawings-canvas-${stageCode}`}
                           // Wave CO — the .pf-drawing-canvas class forces
                           // inline SVGs (which the backend ships with
                           // viewBox-only sizing) to fill the wrapper.
                           className="pf-drawing-canvas"
                           style={{ position: "absolute",
                              left: "50%", top: "50%",
                              transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                              transformOrigin: "center center",
                              width: "min(96%, 1400px)",
                              maxHeight: "92%",
                              pointerEvents: "none",
                              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.18))" }}
                           // Backend produces sanitised, self-contained
                           // SVG (no scripts, no remote refs); rendering
                           // via innerHTML is the standard pattern used
                           // by every other engineering viewer here
                           // (PFD/PID legacy, SLD register, etc.).
                           dangerouslySetInnerHTML={{ __html: current.content_md }} />
                  ) : (
                     <div style={{ position: "absolute", inset: 0,
                                      display: "flex", alignItems: "center",
                                      justifyContent: "center",
                                      color: COL.dim, fontSize: 13 }}>
                        Select a drawing from the&nbsp;
                        <b style={{ color: COL.spark, marginLeft: 4 }}>
                           Documents
                        </b>
                        &nbsp;tab.
                     </div>
                  )}
               </div>
            </div>
         )}
      </div>
   );
}

// ── styles ──
function tabBtn(active) {
   return {
      background: active ? COL.spark : "transparent",
      color: active ? "#020617" : COL.dim,
      border: `1px solid ${active ? COL.spark : COL.border}`,
      padding: "6px 12px", borderRadius: 4, cursor: "pointer",
      fontSize: 12, fontWeight: 600, fontFamily: mono.fontFamily,
   };
}
const btnSecondary = {
   background: "transparent", color: COL.text,
   border: `1px solid ${COL.border}`, padding: "6px 12px",
   borderRadius: 3, cursor: "pointer", fontSize: 12,
   fontFamily: mono.fontFamily,
};
const btnSmall = {
   background: "transparent", color: COL.spark,
   border: `1px solid ${COL.spark}`, padding: "4px 10px",
   borderRadius: 3, cursor: "pointer", fontSize: 11,
   fontFamily: mono.fontFamily,
};
const iconBtn = {
   background: "transparent", color: COL.text,
   border: `1px solid ${COL.border}`, padding: 5,
   borderRadius: 3, cursor: "pointer",
   display: "inline-flex", alignItems: "center",
   justifyContent: "center", fontSize: 11,
   fontFamily: mono.fontFamily,
};
const selectStyle = {
   background: COL.surface, color: COL.text,
   border: `1px solid ${COL.spark}`, padding: "5px 8px",
   borderRadius: 3, cursor: "pointer", fontSize: 11,
   maxWidth: 480,
   fontFamily: mono.fontFamily, outline: "none",
};
const thStyle = {
   textAlign: "left", padding: "10px 16px", color: COL.dim,
   fontWeight: 600, fontSize: 11, letterSpacing: 0.5,
   borderBottom: `1px solid ${COL.border}`,
};
const tdStyle = { padding: "12px 16px", verticalAlign: "top", fontSize: 12 };
